import { name } from "./__init__";
import { createSelector } from "reselect";
import { selectors as appSelectors } from "../../containers/App";
import { selectors as reservationSelectors } from "../../containers/ReservationManager";
import constants from "../../utils/constants";
import { fromJS } from "immutable";
import moment from "moment";
import {
  extrasQuestionnaireSelector,
  reservationARIMapSelector,
} from "../ReservationManager/selectors";
import { hideObligatoryExtrasSelector } from "../App/selectors";

const selectors = {
  ...appSelectors,
  ...reservationSelectors,
};

// getModelSelector :: State -> Model
export const getModelSelector = state => state[name];
/* TODO: CHANGE PROPERTY ARISELECTOR FOR RESERVATION ARI SELECTOR */

export const paymentTypeSelector = createSelector(
  selectors.propertyTickerSelector,
  selectors.selectedPaymentSelector,
  (propertyTicker, selectedPayment) => {
    return (
      propertyTicker &&
      selectedPayment &&
      selectedPayment.get(propertyTicker) &&
      selectedPayment.get(propertyTicker).get("type")
    );
  }
);

export const paymentTickerSelector = createSelector(
  selectors.propertyTickerSelector,
  selectors.selectedPaymentSelector,
  (propertyTicker, selectedPayment) => {
    return (
      propertyTicker &&
      selectedPayment &&
      selectedPayment.get(propertyTicker) &&
      selectedPayment.get(propertyTicker).get("ticker")
    );
  }
);
export const showCheckoutFormSelector = createSelector(
  selectors.configurationSelector,
  configuration => {
    return configuration && configuration.get("showCheckoutForm");
  }
);

export const customerFormFieldsConfiguration = createSelector(
  selectors.configurationSelector,
  configuration => {
    if (!configuration) {
      return null;
    }
    return fromJS({
      name: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      lastName: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      email: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.EMAIL,
      },
      comments: {
        active: true,
        required: false,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      country: {
        active: configuration.get("bookingFormCountry") || false,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      phone: {
        active: configuration.get("bookingFormPhone") || false,
        required: true,
        type: constants.FORM_FIELD_TYPES.PHONE,
      },
      phonePrefix: {
        active: configuration.get("bookingFormPhone") || false,
        required: false,
        type: constants.FORM_FIELD_TYPES.PHONE_PREFIX,
      },
      idDocument: {
        active: configuration.get("bookingFormDni") || false,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      address: {
        active: configuration.get("bookingFormAddress") || false,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      repeatEmail: {
        active: configuration.get("bookingFormRepeatEmail") || false,
        required: true,
        type: constants.FORM_FIELD_TYPES.EMAIL,
      },
      newsletter: {
        active: configuration.get("bookingFormNewsletter") || false,
        required: false,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      arrivalTime: {
        active: configuration.get("bookingFormArrivalTime") || false,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
    });
  }
);

export const customerFormFieldsSelector = createSelector(
  customerFormFieldsConfiguration,
  fieldConfiguration => {
    if (!fieldConfiguration) {
      return null;
    }
    const fields = {
      name: true,
      lastName: true,
      email: true,
      comments: true,
      country: fieldConfiguration.get("country").get("active"),
      phone: fieldConfiguration.get("phone").get("active"),
      phonePrefix: fieldConfiguration.get("phone").get("active"),
      idDocument: fieldConfiguration.get("idDocument").get("active"),
      address: fieldConfiguration.get("address").get("active"),
      repeatEmail: fieldConfiguration.get("repeatEmail").get("active"),
      newsletter: fieldConfiguration.get("newsletter").get("active"),
      arrivalTime: fieldConfiguration.get("arrivalTime").get("active"),
    };
    return {
      fields: Object.keys(fields).filter(fieldName => fields[fieldName]),
      fieldConfiguration,
    };
  }
);

export const paymentFormFieldsConfiguration = createSelector(
  selectors.configurationSelector,
  paymentTypeSelector,
  (configuration, paymentType) => {
    if (!configuration) {
      return null;
    }
    const isCCNumberRequired = true;
    const isSecurityCodeRequired =
      paymentType === constants.PAYMENT_TYPES.PCI_PROXY && configuration.get("bookingFormCcv");
    return fromJS({
      ccHolderName: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      ccType: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      ccNumber: {
        active: true,
        required: isCCNumberRequired,
        type: constants.FORM_FIELD_TYPES.CREDIT_CARD_NUMBER,
      },
      securityCode: {
        active: isSecurityCodeRequired,
        required: isSecurityCodeRequired,
        type: constants.FORM_FIELD_TYPES.CREDIT_CARD_CCV,
      },
      expirationMonth: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      expirationYear: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.STRING,
      },
      hasAcceptedConditions: {
        active: true,
        required: true,
        type: constants.FORM_FIELD_TYPES.BOOLEAN,
      },
    });
  }
);

export const paymentFormFieldsSelector = createSelector(
  paymentFormFieldsConfiguration,
  selectors.selectedPaymentSelector,
  selectors.propertyTickerSelector,
  (fieldConfiguration, selectedPayment, propertyTicker) => {
    if (!fieldConfiguration) {
      return null;
    }
    const actualType =
      selectedPayment &&
      selectedPayment.get(propertyTicker) &&
      selectedPayment.get(propertyTicker).get("type");
    let fields = null;
    if (
      actualType === constants.PAYMENT_TYPES.CREDIT_CARD_VAULT ||
      actualType === constants.PAYMENT_TYPES.CREDIT_CARD_VAULT_EMERGENT
    ) {
      fields = {
        ccHolderName: true,
        ccType: true,
        ccNumber: true,
        securityCode: false,
        expirationMonth: true,
        expirationYear: true,
        hasAcceptedConditions: true,
      };
    } else if (actualType === constants.PAYMENT_TYPES.PCI_PROXY) {
      fields = {
        ccHolderName: true,
        ccType: true,
        ccNumber: true,
        expirationMonth: true,
        expirationYear: true,
        securityCode: fieldConfiguration.get("securityCode").get("active"),
        hasAcceptedConditions: true,
      };
    } else {
      fields = { hasAcceptedConditions: true };
    }
    return {
      fields: Object.keys(fields).filter(fieldName => fields[fieldName]),
      fieldConfiguration,
    };
  }
);

export const ccFormFieldsSelector = createSelector(
  selectors.selectedPaymentSelector,
  selectors.propertyTickerSelector,
  (selectedPayment, propertyTicker) => {
    const fields = {
      ccHolderName: true,
      ccType: true,
      ccNumber: true,
      securityCode: true,
      expirationMonth: true,
      expirationYear: true,
      hasAcceptedConditions: true,
    };
    return (
      selectedPayment &&
      selectedPayment.get(propertyTicker) &&
      selectedPayment.get(propertyTicker).get("type") ===
        constants.PAYMENT_TYPES.CREDIT_CARD_VAULT &&
      Object.keys(fields).filter(fieldName => fields[fieldName])
    );
  }
);

export const pciProxyResponseSelector = createSelector(getModelSelector, model => {
  return model && model.get("pciProxyResponse");
});

export const pciProxyErrorSelector = createSelector(getModelSelector, model => {
  return model && model.get("pciProxyError");
});

export const reservationResourceSelector = createSelector(getModelSelector, model => {
  return model && model.get("reservationResource");
});

export const reservationIDSelector = createSelector(getModelSelector, model => {
  return (
    model &&
    typeof model.get("reservationResource") === "string" &&
    model.get("reservationResource").split("/").pop()
  );
});

export const loadingSelector = createSelector(getModelSelector, model => {
  return model && model.get("loading");
});

export const serviceHasImageSelector = createSelector(selectors.cartSelector, cart => {
  if (cart && cart.reservations) {
    const result = {};
    cart.reservations.map(reservation => {
      if (
        reservation &&
        reservation.get("inventory") &&
        reservation.get("inventory").get("services")
      ) {
        result[reservation.get("inventory").get("ticker")] = reservation
          .get("inventory")
          .get("services")
          .reduce((accumulator, service) => {
            return accumulator || (service.get("media") && service.get("media").size > 0);
          }, false);
      }
    });
    return result;
  }
  return false;
});

export const earliestCancellationDateSelector = createSelector(
  selectors.cartSelector,
  selectors.cancellationRestrictionsCheckoutSelector,
  (cart, reservationsRestrictions) => {
    if (cart && cart.reservations && reservationsRestrictions) {
      let result = null;
      let lIndex = undefined;
      reservationsRestrictions.forEach((reservationRestrictions, index) => {
        const reservationCondition = cart.reservations.getIn([
          index,
          "inventory",
          "condition",
          "ticker",
        ]);

        if (reservationRestrictions[reservationCondition]) {
          reservationRestrictions[reservationCondition].forEach(constraint => {
            // If any of the constraint doesn't contain penalty and
            // result is not set or the new date is before the one saved in result
            if (
              (reservationRestrictions[reservationCondition].some(it => !it.penalty) && !result) ||
              moment(constraint.maxRelease).isBefore(moment(result))
            ) {
              result = constraint.maxRelease;
              lIndex = index;
            }
          });
        }
      });
      return (
        // lIndex is the index of reservationsRestrictions that has no penalty or
        // penalty 0 and the most restrictive release date
        lIndex !== undefined &&
        reservationsRestrictions[lIndex][
          cart.reservations.getIn([lIndex, "inventory", "condition", "ticker"])
        ]
      );
    }
  }
);

export const servicesCollapsedSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("servicesCollapsed")
);

export const legalInfoSelector = createSelector(
  selectors.propertyTickerSelector,
  selectors.propertyDataSelector,
  (propertyTicker, propertyData) =>
    propertyData &&
    propertyData.get(propertyTicker) &&
    propertyData.get(propertyTicker).get("legalInfo")
);

export const communicationsDisclaimerSelector = createSelector(legalInfoSelector, legalInfo => {
  let communicationsDisclaimer = null;

  if (legalInfo) {
    communicationsDisclaimer = legalInfo.find(
      value => value.get("property") === constants.LEGAL_INFO.COMMUNICATIONS_POLICY
    );
    communicationsDisclaimer = communicationsDisclaimer && communicationsDisclaimer.get("text");
  }

  return communicationsDisclaimer;
});

export const privacyPolicySelector = createSelector(legalInfoSelector, legalInfo => {
  let privacyPolicy = null;

  if (legalInfo) {
    privacyPolicy = legalInfo.find(
      value => value.get("property") === constants.LEGAL_INFO.PRIVACY_POLICY
    );
    privacyPolicy = privacyPolicy && privacyPolicy.get("text");
  }

  return privacyPolicy;
});

export const reservationsDatesSelector = createSelector(selectors.cartSelector, cart => {
  if (cart && cart.reservations) {
    const reservationsDates = [];
    cart.reservations.map(reservation => {
      const endDate = moment(reservation.get("endDate"));
      for (
        let currDate = moment(reservation.get("startDate"));
        currDate.isBefore(endDate) || currDate.isSame(endDate);
        currDate.add(1, "days")
      ) {
        if (!reservationsDates.includes(date => date.value === currDate)) {
          reservationsDates.push({
            value: currDate.format("YYYY-MM-DD"),
            label: currDate.format("YYYY-MM-DD"),
          });
        }
      }
    });
    return reservationsDates;
  }
});

export const sortedServicesSelector = createSelector(
  selectors.cartSelector,
  selectors.currencySelector,
  reservationARIMapSelector,
  selectors.propertyTickerSelector,
  (cart, currency, reservationARIMap, propertyTicker) => {
    if (cart && cart.reservations && currency) {
      const returnedSortedServices = [];
      cart.reservations.map(reservation => {
        let sortedServices = reservation.get("inventory").get("services");

        if (sortedServices.filter) {
          sortedServices = sortedServices.filter(el => {
            const availabilityPerDay =
              reservationARIMap.get(reservation.get("id")) &&
              reservationARIMap.get(reservation.get("id")).get(propertyTicker) &&
              reservationARIMap
                .get(reservation.get("id"))
                .get(propertyTicker)
                .get("arivalues")
                .get(el.get("ticker")) &&
              reservationARIMap
                .get(reservation.get("id"))
                .get(propertyTicker)
                .get("arivalues")
                .get(el.get("ticker"))
                .get("availabilityPerDay");
            return Boolean(!availabilityPerDay);
          });
        }
        if (sortedServices.sortBy) {
          sortedServices = sortedServices.sortBy(service => service.get("order"));
        }

        const services = [];
        sortedServices.forEach(el => {
          /* TODO: pass currency from config */
          /* TODO: use real images!  */
          const media = el.get("media") ? el.get("media").toJS() : [];
          const entity = {
            ticker: el.get("ticker"),
            title: el.get("name"),
            minPrice: el.get("totalRate"),
            maxPrice: el.get("totalRate"),
            unit: el.get("type"),
            type: el.get("type"),
            obligatory: el.get("obligatory"),
            currency: currency,
            showFooter: false,
            showInfo: true,
            hasCheckBox: el.get("type") !== constants.SERVICE_TYPE.UNIT,
            thumbnail: media && media.length > 0 ? media[0].url : undefined,
            hasImage: media && media.length > 0,
            media:
              media && media.length > 0
                ? media.map(img => ({
                    ...img,
                    url: img.url,
                    description: el.get("description"),
                  }))
                : [],
            description: el.get("description"),
            statusCode: constants.BOOKABLE_STATUS.AVAILABLE,
            selectOptions:
              el.get("type") === constants.SERVICE_TYPE.UNIT &&
              new Array(el.get("maxUnits")).fill(false).map((ign, idx) => ({
                key: idx + 1,
                value: idx + 1,
              })),
            dateSelectionType: el.get("dateSelectionType"),
            totalRatePerDay:
              reservationARIMap.get(reservation.get("id")) &&
              reservationARIMap.get(reservation.get("id")).get(propertyTicker) &&
              reservationARIMap
                .get(reservation.get("id"))
                .get(propertyTicker)
                .get("arivalues")
                .get(el.get("ticker")) &&
              reservationARIMap
                .get(reservation.get("id"))
                .get(propertyTicker)
                .get("arivalues")
                .get(el.get("ticker"))
                .get("totalRatePerDay"),

            appliesToGuests: el.get("appliesToGuests"),
          };

          services.push(entity);
        });
        returnedSortedServices.push(services);
      });
      return returnedSortedServices;
    }
  }
);

export const showExtrasModalSelector = createSelector(
  sortedServicesSelector,
  extrasQuestionnaireSelector,
  (sortedServices, extrasQuestionnaire) => {
    const returnedSortedServices = [];
    if (sortedServices && extrasQuestionnaire) {
      sortedServices.map(service => {
        const isExtraObligatory = service.reduce(
          (accum, it) => ({
            ...accum,
            [it.ticker]: it.obligatory,
          }),
          {}
        );
        const extrasWithObligatoryForms =
          extrasQuestionnaire &&
          extrasQuestionnaire.reduce(
            (accum, it) =>
              it.fields.some(field => Object.keys(field.constraints).length !== 0)
                ? it.dependers.reduce((accum2, it2) => accum2 + it2.tickers, accum + "")
                : "",
            ""
          );

        const showExtrasModal = Object.keys(isExtraObligatory).some(
          ticker =>
            isExtraObligatory[ticker] &&
            extrasWithObligatoryForms &&
            extrasWithObligatoryForms.includes(ticker)
        );

        returnedSortedServices.push(showExtrasModal);
      });
    }
    return returnedSortedServices;
  }
);

export const hideDefaultPrivacyPolicySelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("hideDefaultPrivacyPolicy")
);

export const pnpV2PaymentOrderSuccessSelector = createSelector(
  getModelSelector,
  model => model && model.get("pnpV2PaymentOrderSuccess")
);

export const pnpV2PaymentOrderErrorSelector = createSelector(
  getModelSelector,
  model => model && model.get("pnpV2PaymentOrderError")
);

export const formAsyncValidationErrorsSelector = createSelector(
  getModelSelector,
  model => model && model.get("formAsyncValidationErrors")
);

export const pnpV2PaymentTokenSelector = createSelector(
  getModelSelector,
  model => model && model.get("pnpV2PaymentToken")
);

export const isSharedBookingSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && !!configuration.get("isSharedBooking")
);

export const showServicesSelector = createSelector(
  isSharedBookingSelector,
  servicesCollapsedSelector,
  selectors.widthSelector,
  (isSharedBooking, servicesCollapsed, width) =>
    !isSharedBooking &&
    (servicesCollapsed === constants.SERVICES_EXPANDED.EXPANDED ||
      (servicesCollapsed === constants.SERVICES_EXPANDED.EXPAND_DESKTOP && width >= 768))
);

export const extrasToShowSelector = createSelector(
  sortedServicesSelector,
  hideObligatoryExtrasSelector,
  (sortedServices, hideObligatoryExtras) =>
    sortedServices &&
    !(
      hideObligatoryExtras &&
      sortedServices.every(servicesArray => servicesArray.every(service => service.obligatory))
    )
);
